// Reattestation flow — section-by-section "still accurate?" / "Update" UI.
// Triggered = one-time $19 review (query param ?triggered=1). Annual = free.
// Spec: Sprint 7 of VOYA9_Frontend_Assessment_Multiprogram_Modes.md
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/Button";
import { Spinner } from "@/components/Loader";
import { Eyebrow, Headline } from "@/components/Editorial";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

export default function Reattestation() {
  const [params] = useSearchParams();
  const triggered = params.get("triggered") === "1";
  const navigate = useNavigate();

  const [sections, setSections] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [confirmed, setConfirmed] = useState({});
  const [updated, setUpdated] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null);

  useEffect(() => {
    apiGet("/consumer/reattestation/sections")
      .then((d) => setSections(Array.isArray(d) ? d : d?.sections || []))
      .catch(() => setLoadErr(true));
  }, []);

  const total = sections?.length || 0;
  const confirmedCount = useMemo(
    () => Object.values(confirmed).filter(Boolean).length + Object.keys(updated).length,
    [confirmed, updated],
  );
  const canSubmit = total > 0 && confirmedCount === total;
  const updateCount = Object.keys(updated).length;

  const goToReview = () => {
    if (!canSubmit) return;
    // If nothing changed, skip the diff preview — it would be empty.
    if (updateCount === 0) return submit();
    setReviewing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await apiPost("/consumer/reattestation/submit", {
        triggered,
        confirmed: Object.entries(confirmed).filter(([, v]) => v).map(([k]) => k),
        updated,
      });
      setSubmittedResult({
        triggered,
        updateCount,
        physicianName: res?.assignedPhysician?.lastName
          ? `Dr. ${res.assignedPhysician.lastName}`
          : (res?.physicianName || null),
        nextReviewDate: res?.nextReviewDate || null,
      });
    } catch (err) {
      toast(err.detail || "Couldn't submit. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadErr) {
    return (
      <div style={{ minHeight: "100vh", background: C.cream, padding: "72px 24px" }}>
        <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
          <Eyebrow color={C.coral}>★ REVIEW UNAVAILABLE</Eyebrow>
          <Headline before="WE HIT A" italic="snag." size={44} className="mt-4" />
          <p style={{ marginTop: 16, color: C.warmGray }}>
            We couldn't load your health profile sections. Try again in a moment.
          </p>
          <div style={{ marginTop: 20 }}>
            <Button variant="primary" testId="reattest-home-btn" onClick={() => navigate("/app")}>BACK TO HOME</Button>
          </div>
        </div>
      </div>
    );
  }

  if (sections === null) {
    return <div style={{ padding: 72, textAlign: "center", background: C.cream, minHeight: "100vh" }}><Spinner /></div>;
  }

  if (submittedResult) {
    return <ReattestationSubmitted result={submittedResult} onHome={() => navigate("/app", { replace: true })} />;
  }

  if (reviewing) {
    return (
      <ReviewDiffScreen
        sections={sections}
        confirmed={confirmed}
        updated={updated}
        triggered={triggered}
        submitting={submitting}
        onBack={() => setReviewing(false)}
        onSubmit={submit}
      />
    );
  }

  return (
    <div data-testid="reattestation-page" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "48px 24px 160px" }}>
        <Eyebrow color={triggered ? C.peachDeep : C.sageDark}>
          {triggered ? "★ QUICK SAFETY REVIEW" : "★ ANNUAL HEALTH CHECK-IN"}
        </Eyebrow>
        <Headline before="Anything" italic="changed?" size={60} className="mt-4" />
        <p style={{ marginTop: 20, fontSize: 15, lineHeight: 1.65, color: C.warmGray, maxWidth: 540 }}>
          Quick review of your health profile. Confirm what's still accurate and update what's changed.
          {triggered && <> <strong style={{ color: C.ink }}>One-time $19 review fee applies.</strong></>}
        </p>

        <div style={{ marginTop: 14, fontSize: 12, fontWeight: 800, letterSpacing: "0.18em", color: C.peachDeep }}>
          {confirmedCount} / {total} CONFIRMED{updateCount > 0 && <> · <span style={{ color: C.peachDark }}>{updateCount} UPDATED</span></>}
        </div>

        <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 14 }}>
          {sections.map((s) => (
            <ReattestSection
              key={s.key}
              section={s}
              editing={editingKey === s.key}
              confirmed={!!confirmed[s.key]}
              updatedPayload={updated[s.key]}
              onConfirm={() => {
                setConfirmed((p) => ({ ...p, [s.key]: true }));
                setUpdated((p) => { const n = { ...p }; delete n[s.key]; return n; });
                setEditingKey(null);
              }}
              onEdit={() => setEditingKey(s.key)}
              onCancelEdit={() => setEditingKey(null)}
              onSaveUpdate={(payload) => {
                setUpdated((p) => ({ ...p, [s.key]: payload }));
                setConfirmed((p) => ({ ...p, [s.key]: false }));
                setEditingKey(null);
              }}
            />
          ))}
        </div>

        <div style={{ marginTop: 40 }}>
          <Button
            variant="primary"
            size="lg"
            block
            testId="reattest-submit-btn"
            onClick={goToReview}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "SUBMITTING…" : (updateCount > 0 ? "REVIEW CHANGES →" : (triggered ? "SUBMIT & PAY $19 →" : "SUBMIT REVIEW →"))}
          </Button>
          {!canSubmit && total > 0 && (
            <p style={{ marginTop: 12, fontSize: 12, color: C.warmGray, textAlign: "center" }}>
              Confirm or update every section to continue.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ReattestSection({ section, editing, confirmed, updatedPayload, onConfirm, onEdit, onCancelEdit, onSaveUpdate }) {
  const [draft, setDraft] = useState(section.summary || "");
  useEffect(() => { if (editing) setDraft(updatedPayload?.value || section.summary || ""); }, [editing, updatedPayload, section.summary]);

  const status = confirmed ? "confirmed" : updatedPayload ? "updated" : "pending";

  return (
    <div
      data-testid={`reattest-section-${section.key}`}
      style={{
        background: status === "pending" ? C.pure : status === "updated" ? C.peach : C.bone,
        border: `1.5px solid ${status === "pending" ? C.warmGray3 : C.peachDeep}`,
        padding: 20,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
            {section.category || "HEALTH PROFILE"}
          </div>
          <div style={{ marginTop: 4, fontSize: 17, fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>
            {section.title}
          </div>
        </div>
        {status === "confirmed" && <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.sageDark }}>✓ CONFIRMED</span>}
        {status === "updated" && <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.peachDark }}>✏ UPDATED</span>}
      </div>

      {!editing && (
        <div style={{ marginTop: 12, fontSize: 14, color: C.ink, lineHeight: 1.55, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
          {updatedPayload?.value || section.summary || <span style={{ color: C.warmGray }}>No information on file.</span>}
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 14 }}>
          <textarea
            data-testid={`reattest-edit-${section.key}`}
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="What's changed since last year?"
            style={{
              width: "100%", padding: "12px 14px", fontSize: 15,
              border: `1.5px solid ${C.ink}`, background: C.pure, color: C.ink,
              fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
            }}
          />
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <Button variant="primary" size="sm" testId={`reattest-save-${section.key}`} onClick={() => onSaveUpdate({ value: draft })}>
              SAVE UPDATE
            </Button>
            <Button variant="ghost" size="sm" testId={`reattest-cancel-${section.key}`} onClick={onCancelEdit}>CANCEL</Button>
          </div>
        </div>
      )}

      {!editing && status !== "confirmed" && (
        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Button variant="primary" size="sm" testId={`reattest-confirm-${section.key}`} onClick={onConfirm}>
            ✓ STILL ACCURATE
          </Button>
          <Button variant="ghost" size="sm" testId={`reattest-update-${section.key}`} onClick={onEdit}>
            UPDATE
          </Button>
        </div>
      )}

      {!editing && status === "confirmed" && (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            data-testid={`reattest-change-${section.key}`}
            onClick={onEdit}
            style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", cursor: "pointer", textDecoration: "underline" }}
          >
            Change
          </button>
        </div>
      )}
    </div>
  );
}


/* =============================================================
 *  ReviewDiffScreen — "what changed this year" preview before submit
 * ============================================================= */
function ReviewDiffScreen({ sections, confirmed, updated, triggered, submitting, onBack, onSubmit }) {
  const updatedSections = sections.filter((s) => updated[s.key]);
  const confirmedSections = sections.filter((s) => confirmed[s.key]);

  return (
    <div data-testid="reattestation-review" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "48px 24px 160px" }}>
        <button
          type="button"
          data-testid="reattest-review-back"
          onClick={onBack}
          style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 12, fontWeight: 800, letterSpacing: "0.18em", cursor: "pointer", padding: 0 }}
        >
          ← EDIT
        </button>

        <div style={{ marginTop: 18 }}>
          <Eyebrow color={C.peachDeep}>★ REVIEW YOUR CHANGES</Eyebrow>
          <Headline
            before="Here's what's"
            italic={updatedSections.length === 1 ? "different." : "changed this year."}
            size={56}
            className="mt-4"
          />
          <p style={{ marginTop: 20, fontSize: 15, lineHeight: 1.65, color: C.warmGray, maxWidth: 540 }}>
            Take a final look before sending this to your physician. You can still go back and edit anything.
          </p>
        </div>

        {/* Diff cards */}
        <div style={{ marginTop: 36, display: "flex", flexDirection: "column", gap: 14 }}>
          {updatedSections.map((s) => {
            const beforeVal = s.summary || "";
            const afterVal = updated[s.key]?.value || "";
            return (
              <div
                key={s.key}
                data-testid={`reattest-diff-${s.key}`}
                style={{ background: C.pure, border: `1.5px solid ${C.peachDeep}`, padding: 20 }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em", color: C.ink }}>
                    {s.title}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDark }}>
                    ✏ UPDATED
                  </span>
                </div>

                <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr", gap: 12 }} className="md:!grid-cols-2">
                  <DiffCell label="LAST YEAR" body={beforeVal || "Nothing on file."} muted />
                  <DiffCell label="NOW" body={afterVal} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Re-confirmed summary */}
        {confirmedSections.length > 0 && (
          <div
            data-testid="reattest-confirmed-summary"
            style={{
              marginTop: 28, padding: 20, background: C.bone,
              display: "flex", alignItems: "flex-start", gap: 14,
            }}
          >
            <span
              aria-hidden
              style={{
                flexShrink: 0, width: 28, height: 28, borderRadius: "50%",
                background: C.sage, color: C.sageDark, fontWeight: 800, fontSize: 15,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              ✓
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.sageDark }}>
                STILL ACCURATE · {confirmedSections.length} SECTION{confirmedSections.length === 1 ? "" : "S"}
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: C.warmGray, lineHeight: 1.55 }}>
                {confirmedSections.map((s) => s.title).join(" · ")}
              </div>
            </div>
          </div>
        )}

        {/* Fee line */}
        {triggered && (
          <div style={{ marginTop: 22, padding: "18px 20px", background: C.ink, color: C.cream }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
              <span style={{ color: "rgba(250,246,240,0.7)" }}>Safety review fee</span>
              <strong style={{ color: C.peach }}>$19.00</strong>
            </div>
          </div>
        )}

        {/* Confirm action */}
        <div style={{ marginTop: 36 }}>
          <Button
            variant="primary"
            size="lg"
            block
            testId="reattest-confirm-submit-btn"
            onClick={onSubmit}
            disabled={submitting}
          >
            {submitting ? "SUBMITTING…" : (triggered ? "CONFIRM & PAY $19 →" : "CONFIRM & SUBMIT →")}
          </Button>
          <p style={{ marginTop: 12, fontSize: 12, color: C.warmGray, textAlign: "center" }}>
            Your physician will see a summary of these changes.
          </p>
        </div>
      </div>
    </div>
  );
}

function DiffCell({ label, body, muted }) {
  return (
    <div style={{ background: muted ? C.bone : C.cream, padding: 14, borderLeft: `3px solid ${muted ? C.warmGray3 : C.peachDeep}` }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: muted ? C.warmGray : C.peachDark }}>
        {label}
      </div>
      <div
        style={{
          marginTop: 8, fontSize: 14, lineHeight: 1.55,
          color: muted ? C.warmGray : C.ink,
          fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic",
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}
      >
        {body}
      </div>
    </div>
  );
}

/* =============================================================
 *  ReattestationSubmitted — warm confirmation screen after submit
 * ============================================================= */
function ReattestationSubmitted({ result, onHome }) {
  const { triggered, updateCount, physicianName, nextReviewDate } = result;
  const phys = physicianName || "your physician";

  return (
    <div data-testid="reattestation-submitted" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "88px 24px 120px", textAlign: "center" }}>
        <div
          aria-hidden
          style={{
            width: 76, height: 76, borderRadius: "50%",
            background: triggered ? C.peach : C.sage,
            color: triggered ? C.peachDark : C.sageDark,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 34, fontWeight: 800,
          }}
        >
          ✓
        </div>

        <div style={{ marginTop: 26 }}>
          <Eyebrow color={triggered ? C.peachDeep : C.sageDark}>
            {triggered ? "★ SAFETY REVIEW SUBMITTED" : "★ REVIEW COMPLETE"}
          </Eyebrow>
        </div>

        <Headline
          before="Thanks for the"
          italic="update."
          size={60}
          className="mt-4"
        />

        <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.65, color: C.warmGray, maxWidth: 440, margin: "24px auto 0" }}>
          {triggered
            ? <>Your health profile is on the way to {phys}. They'll review within 24 hours and reach out about your new program.</>
            : updateCount > 0
              ? <>We've sent your {updateCount === 1 ? "change" : `${updateCount} changes`} to {phys}. They'll take a look when they're next with your file.</>
              : <>Your health profile is confirmed for the year. Nothing else to do.</>
          }
        </p>

        {nextReviewDate && !triggered && (
          <div
            data-testid="reattest-next-review"
            style={{
              marginTop: 30, display: "inline-block",
              padding: "12px 18px", background: C.bone,
              fontSize: 12, fontWeight: 800, letterSpacing: "0.18em", color: C.warmGray,
            }}
          >
            NEXT ANNUAL CHECK-IN · {new Date(nextReviewDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }).toUpperCase()}
          </div>
        )}

        <div style={{ marginTop: 40 }}>
          <Button variant="primary" size="lg" testId="reattest-submitted-home-btn" onClick={onHome}>
            BACK TO HOME →
          </Button>
        </div>
      </div>
    </div>
  );
}

